import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "messages", "typing", "sendButton", "scannerModal", "helpSidebar", "helpPanel"]
  static values = {
    conversationId: Number,
    messagesUrl: String
  }

  connect() {
    this.scrollToBottom()
    // Auto-focus the input on page load
    setTimeout(() => {
      this.focusInput()
    }, 100)

    // Observe for new messages added via Turbo Stream
    this.setupMessageObserver()
  }

  setupMessageObserver() {
    const messagesContainer = document.getElementById("chat-messages")
    if (!messagesContainer) return

    this.messageObserver = new MutationObserver((mutations) => {
      // Check if new child nodes were added
      const hasNewNodes = mutations.some(m => m.addedNodes.length > 0)
      if (hasNewNodes) {
        this.scrollToBottom()
      }
    })

    this.messageObserver.observe(messagesContainer, { childList: true })
  }

  disconnect() {
    if (this.messageObserver) {
      this.messageObserver.disconnect()
    }
  }

  focusInput() {
    if (this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  autoResize() {
    const textarea = this.inputTarget
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'
    // Set to scrollHeight but respect max-height
    const newHeight = Math.min(textarea.scrollHeight, 200)
    textarea.style.height = `${newHeight}px`
  }

  handleKeydown(event) {
    // Enter without shift sends the message
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage(event)
    }
    // Shift+Enter adds a new line (default behavior)
  }

  async sendMessage(event) {
    event.preventDefault()

    const content = this.inputTarget.value.trim()
    if (!content) return

    this.inputTarget.value = ""
    // Reset textarea height after clearing
    this.inputTarget.style.height = '48px'
    this.addUserMessage(content)
    this.showTyping()
    this.disableSend()

    try {
      const response = await fetch(this.messagesUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: new URLSearchParams({ content: content })
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        this.addErrorMessage("Failed to send message. Please try again.")
      }
    } catch (error) {
      console.error("Send message error:", error)
      this.addErrorMessage("Connection error. Please try again.")
    } finally {
      this.hideTyping()
      this.enableSend()
      this.scrollToBottom()
      // Re-focus input after AI responds
      this.focusInput()
    }
  }

  addUserMessage(content) {
    const messagesContainer = document.getElementById("chat-messages")

    // Remove the empty state if present
    const emptyState = document.getElementById("chat-empty-state")
    if (emptyState) {
      emptyState.remove()
    }

    const messageHtml = `
      <div class="flex justify-end">
        <div class="max-w-[80%] bg-beige-700 text-white rounded-2xl rounded-br-sm px-4 py-3">
          <p class="whitespace-pre-wrap">${this.escapeHtml(content)}</p>
        </div>
      </div>
    `
    messagesContainer.insertAdjacentHTML("beforeend", messageHtml)
    this.scrollToBottom()
  }

  addErrorMessage(content) {
    const messagesContainer = document.getElementById("chat-messages")
    const messageHtml = `
      <div class="flex justify-start">
        <div class="flex items-start gap-3 max-w-[90%]">
          <div class="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="bg-red-50 border border-red-200 rounded-2xl rounded-tl-sm px-4 py-3">
            <p class="text-red-700">${this.escapeHtml(content)}</p>
          </div>
        </div>
      </div>
    `
    messagesContainer.insertAdjacentHTML("beforeend", messageHtml)
    this.scrollToBottom()
  }

  showTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.remove("hidden")
      this.scrollToBottom()
    }
  }

  hideTyping() {
    if (this.hasTypingTarget) {
      this.typingTarget.classList.add("hidden")
    }
  }

  disableSend() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
      this.sendButtonTarget.classList.add("opacity-50")
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
  }

  enableSend() {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false
      this.sendButtonTarget.classList.remove("opacity-50")
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
    }
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        this.messagesTarget.scrollTo({
          top: this.messagesTarget.scrollHeight,
          behavior: 'smooth'
        })
      })
    }
  }

  useSuggestion(event) {
    const suggestion = event.currentTarget.dataset.suggestion
    this.inputTarget.value = suggestion
    this.autoResize()
    this.focusInput()
  }

  useSuggestionAndClose(event) {
    this.useSuggestion(event)
    this.toggleHelp()
  }

  toggleScanner() {
    if (this.hasScannerModalTarget) {
      this.scannerModalTarget.classList.toggle("hidden")
    }
  }

  closeScanner() {
    if (this.hasScannerModalTarget) {
      this.scannerModalTarget.classList.add("hidden")
    }
  }

  toggleHelp() {
    if (this.hasHelpPanelTarget) {
      this.helpPanelTarget.classList.toggle("hidden")
    }
  }

  handleIsbnScanned(event) {
    const isbn = event.detail?.isbn || event.detail
    if (isbn) {
      this.closeScanner()
      this.inputTarget.value = `Look up ISBN ${isbn} and add it to my library`
      this.autoResize()
      this.focusInput()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
